import { Injectable } from "@angular/core";
import { TokenRepo } from "@app/core/shared/domain/token.repo";
import {
  BehaviorSubject,
  catchError,
  firstValueFrom,
  from,
  Observable,
  of,
  shareReplay,
  tap,
  throwError,
} from "rxjs";
import { User } from "@app/core/user/domain/user";
import { UserRepo } from "@app/core/user/domain/repositories/user.repo";
import { MissingCredentialsError } from "@app/core/shared/domain/missing-credentials.error";

@Injectable({
  providedIn: "root",
})
export class GetUserProfileUseCase {
  constructor(
    private tokenRepo: TokenRepo,
    private userRepo: UserRepo
  ) {}

  private cache$ = new BehaviorSubject<User | null>(null);
  private inFlight$: Observable<User> | null = null;

  execute(): Observable<User> {
    const cached = this.cache$.value;
    if (cached !== null) {
      return of(cached);
    }
    if (this.inFlight$ !== null) {
      return this.inFlight$;
    }
    this.inFlight$ = from(this.getUserProfile()).pipe(
      tap({
        next: (user) => {
          this.cache$.next(user);
          this.inFlight$ = null;
        },
      }),
      catchError((error) => {
        this.inFlight$ = null;
        return throwError(() => error);
      }),
      shareReplay(1)
    );
    return this.inFlight$;
  }

  setState(user: User): void {
    this.cache$.next(user);
  }

  invalidate(): void {
    this.cache$.next(null);
    this.inFlight$ = null;
  }

  private async getUserProfile(): Promise<User> {
    // Take the temporal token if it exists, otherwise take the normal token
    const token = this.tokenRepo.getTemporal() ?? this.tokenRepo.get();
    if (token == null) {
      //If the token is null, it means that the user has not logged before or the user has logged out
      throw new MissingCredentialsError();
    }
    return firstValueFrom(this.userRepo.get(token));
  }
}
